import {
  type GroupClassIllustrations,
  ChakraBox,
  GroupListViewCardV2,
  SkeletonLoader,
} from '@equip.health/ui';
import GroupSectionsList from '~/components/maud/calendar/groupsV2/common/GroupSections';
import {
  type GroupTypeEnum,
  GroupTypeTagColorMapper,
} from '~/lib/constants/groupClasses.constants';
import { SYSTEM_TIME_ZONE } from '~/lib/util/calendar.util';
import { useGetGroupClassDetailsV2Query } from '~/store/services/groupClass';

interface SectionsListProps {
  groupId: string;
  excludeSectionId?: string;
  selectedSection?: SectionData;
  onSectionSelect: (section: SectionData) => void;
}

const PADDING_AND_GAPS = 157;
const GROUP_LIST_CARD_HEIGHT = 114; // Max group list card height observed.
const CONTAINER_HEIGHT = `calc(72vh - ${PADDING_AND_GAPS}px)`;
const SECTIONS_HEIGHT = `calc(${CONTAINER_HEIGHT} - ${GROUP_LIST_CARD_HEIGHT}px)`;
const SectionsList = ({
  groupId,
  excludeSectionId,
  onSectionSelect,
  selectedSection,
}: SectionsListProps) => {
  const { data: groupDetails, isLoading: isFetchingV2 } =
    useGetGroupClassDetailsV2Query(groupId, {
      skip: !groupId,
    });

  const props = {
    ...groupDetails,
    groupTypeTagColor:
      GroupTypeTagColorMapper[groupDetails?.groupTypeCode as GroupTypeEnum],
    groupType: groupDetails?.groupType,
    iconName: groupDetails?.iconName as GroupClassIllustrations,
    modalities: groupDetails?.treatmentModalities,
    programs: groupDetails?.treatmentPrograms,
  };

  const isLoaded = !isFetchingV2 && groupDetails;
  const visibleSections = (groupDetails?.sections || []).filter(
    (section) => section.id !== excludeSectionId,
  );

  return (
    <ChakraBox height={CONTAINER_HEIGHT}>
      <ChakraBox marginTop="12px" padding="0px 5px 5px">
        <SkeletonLoader
          id={`${groupId}__group-list-view-card`}
          isLoaded={!!isLoaded}
          height="100%"
          width="100%"
        >
          <GroupListViewCardV2 {...props} />
        </SkeletonLoader>
        <SkeletonLoader
          id={`${groupId}__sections`}
          isLoaded={!!isLoaded}
          height="100%"
          width="100%"
        >
          <GroupSectionsList
            id={`${groupId}__group-sections`}
            groupSections={visibleSections}
            selectedTimezone={SYSTEM_TIME_ZONE}
            getKebabOptions={() => []}
            height={SECTIONS_HEIGHT}
            onCardClick={onSectionSelect}
            selectedSection={selectedSection}
          />
        </SkeletonLoader>
      </ChakraBox>
    </ChakraBox>
  );
};

export default SectionsList;
